// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
// ✅ MIGRATED TO TanStack Query — Component → Hook → Service pattern
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Lock, Eye, Palette, FileText, FileCheck } from 'lucide-react';
import { useAuthStore } from '@/features/auth';
import { Button, Spinner, Badge } from '@/components/ui-v2';
import { TemplatePreviewModal } from './TemplatePreviewModal';
import { useAvailableTemplates, useCurrentTemplates, useSaveTemplatePreferences } from '../hooks/useTemplates';
import type { DocumentTemplate } from '../services/templates.service';

export const TemplateSelector: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  // TanStack Query hooks (remplace useState + useEffect + API directe)
  const { data: templates = [], isLoading: loadingTemplates } = useAvailableTemplates();
  const { data: currentTemplates, isLoading: loadingCurrent } = useCurrentTemplates();
  const savePreferences = useSaveTemplatePreferences();

  // UI state only
  const [selectedInvoiceTemplate, setSelectedInvoiceTemplate] = useState<string | null>(null);
  const [selectedQuoteTemplate, setSelectedQuoteTemplate] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'invoice' | 'quote'>('invoice');
  const [previewTemplate, setPreviewTemplate] = useState<DocumentTemplate | null>(null);

  // Sync selected templates when current templates load
  useEffect(() => {
    if (currentTemplates) {
      setSelectedInvoiceTemplate(currentTemplates.invoiceTemplateId);
      setSelectedQuoteTemplate(currentTemplates.quoteTemplateId);
    }
  }, [currentTemplates]);

  // V2 Card classes
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl hover:-translate-y-1
    transition-all duration-500`;

  const handleSaveTemplates = useCallback(() => {
    if (!selectedInvoiceTemplate || !selectedQuoteTemplate) {
      return;
    }
    savePreferences.mutate({
      invoiceTemplateId: selectedInvoiceTemplate,
      quoteTemplateId: selectedQuoteTemplate,
    });
  }, [selectedInvoiceTemplate, selectedQuoteTemplate, savePreferences]);

  const getTemplatesForType = useCallback((type: 'invoice' | 'quote') => {
    const templateType = type === 'invoice' ? 'INVOICE' : 'QUOTE';
    return templates.filter(t => t.type === templateType);
  }, [templates]);

  const renderTemplateCard = (template: DocumentTemplate, type: 'invoice' | 'quote') => {
    const isSelected = type === 'invoice'
      ? selectedInvoiceTemplate === template.id
      : selectedQuoteTemplate === template.id;

    const isCurrent = type === 'invoice'
      ? currentTemplates?.invoiceTemplateId === template.id
      : currentTemplates?.quoteTemplateId === template.id;

    const canUse = template.canUse !== false;

    return (
      <div
        key={template.id}
        onClick={() => {
          if (canUse) {
            if (type === 'invoice') {
              setSelectedInvoiceTemplate(template.id);
            } else {
              setSelectedQuoteTemplate(template.id);
            }
          }
        }}
        className={`
          relative bg-white/20 backdrop-blur-xl rounded-2xl p-5 cursor-pointer transition-all duration-300
          border-2
          ${isSelected
            ? 'border-teal-500 bg-teal-50/30 shadow-lg shadow-teal-200/30'
            : 'border-white/30 hover:border-teal-300 hover:bg-white/40'}
          ${!canUse ? 'opacity-60 cursor-not-allowed' : ''}
          ${isCurrent ? 'ring-2 ring-teal-400 ring-offset-2' : ''}
        `}
      >
        {/* Header avec badges et bouton preview */}
        <div className="flex items-center justify-between mb-2">
          {/* Badge Current */}
          <div className="flex items-center gap-2">
            {isCurrent && (
              <Badge variant="success">
                <Check className="w-3 h-3 mr-1" />
                {t('templates.selector.current')}
              </Badge>
            )}
          </div>

          {/* Preview button + Badge Premium */}
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setPreviewTemplate(template);
              }}
              className="p-2 bg-white/80 hover:bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 group"
              title={t('templates.selector.previewTooltip')}
            >
              <Eye className="w-4 h-4 text-gray-500 group-hover:text-teal-600" />
            </button>

            {template.isPremium && (
              <div className={`px-2.5 py-1 rounded-xl text-xs font-semibold flex items-center gap-1 ${
                canUse
                  ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {canUse ? <Palette className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {canUse ? t('templates.selector.premium') : t('templates.selector.locked')}
              </div>
            )}
          </div>
        </div>

        {/* Couleurs du template */}
        {template.styleConfig?.primaryColor && (
          <div className="flex gap-2 mb-4 mt-4">
            <div
              className="w-14 h-14 rounded-xl border-2 border-white/50 shadow-md"
              style={{ backgroundColor: template.styleConfig.primaryColor }}
              title={`Couleur principale: ${template.styleConfig.primaryColor}`}
            />
            {template.styleConfig.secondaryColor && (
              <div
                className="w-14 h-14 rounded-xl border-2 border-white/50 shadow-md"
                style={{ backgroundColor: template.styleConfig.secondaryColor }}
                title={`Couleur secondaire: ${template.styleConfig.secondaryColor}`}
              />
            )}
          </div>
        )}

        {/* Informations */}
        <h3 className="font-bold text-lg text-gray-900 mb-2 mt-2 flex items-center gap-2">
          {template.displayName || template.name}
          {isSelected && (
            <Check className="w-5 h-5 text-teal-600" />
          )}
        </h3>

        {template.description && (
          <p className="text-sm text-gray-600 mb-4">{template.description}</p>
        )}

        {/* Caractéristiques */}
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-2.5 py-1 bg-white/50 backdrop-blur-sm rounded-lg text-gray-700 border border-white/50">
            {template.type}
          </span>
          {template.isSystemTemplate && (
            <span className="px-2.5 py-1 bg-teal-50 rounded-lg text-teal-700 border border-teal-200/50">
              {t('templates.selector.system')}
            </span>
          )}
        </div>

        {/* Niveau requis */}
        {template.isPremium && (
          <div className="mt-4 pt-4 border-t border-white/30">
            <p className="text-xs text-gray-500">
              {t('templates.selector.requiredPlan')}: <span className="font-semibold text-teal-700">{template.minPlanLevel}</span>
            </p>
            {!canUse && (
              <p className="text-xs text-red-600 mt-1">
                {t('templates.selector.upgradePlan')}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  const isLoading = loadingTemplates || loadingCurrent;

  if (isLoading) {
    return (
      <div className="px-8 py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-8 pt-8 pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-2xl">
          <Palette className="w-8 h-8 text-teal-600" />
        </div>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('templates.selector.title')}</h1>
          <p className="text-gray-500 mt-0.5">
            {t('templates.selector.subtitle')}
          </p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-teal-50/30 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-5 hover:bg-teal-50/50 transition-all duration-300">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Eye className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h4 className="font-semibold text-teal-900">{t('templates.selector.customizeInfo')}</h4>
            <p className="text-sm text-teal-800 mt-1">
              {t('templates.selector.customizeDescription')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className={cardClasses}>
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('invoice')}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-semibold transition-all duration-300 ${
              activeTab === 'invoice'
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-200/50'
                : 'bg-white/30 text-gray-600 hover:bg-white/50 hover:text-gray-900'
            }`}
          >
            <FileText className="w-5 h-5" />
            {t('templates.selector.invoiceTemplates')}
          </button>
          <button
            onClick={() => setActiveTab('quote')}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-semibold transition-all duration-300 ${
              activeTab === 'quote'
                ? 'bg-teal-600 text-white shadow-lg shadow-teal-200/50'
                : 'bg-white/30 text-gray-600 hover:bg-white/50 hover:text-gray-900'
            }`}
          >
            <FileCheck className="w-5 h-5" />
            {t('templates.selector.quoteTemplates')}
          </button>
        </div>

        {/* Template Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
          {getTemplatesForType(activeTab).length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="p-4 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
                <Palette className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('templates.selector.emptyState')}
              </h3>
              <p className="text-gray-500">
                {activeTab === 'invoice' ? t('templates.selector.emptyInvoice') : t('templates.selector.emptyQuote')}
              </p>
            </div>
          ) : (
            activeTab === 'invoice'
              ? getTemplatesForType('invoice').map(template => renderTemplateCard(template, 'invoice'))
              : getTemplatesForType('quote').map(template => renderTemplateCard(template, 'quote'))
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-6 border-t border-white/30">
          <div className="text-sm text-gray-600 space-y-1">
            {currentTemplates && (
              <>
                <p className="flex items-center gap-2">
                  <span className="font-medium text-gray-700">{t('templates.selector.invoiceLabel')}:</span>
                  <Badge>{currentTemplates.invoiceTemplateDisplayName}</Badge>
                </p>
                <p className="flex items-center gap-2">
                  <span className="font-medium text-gray-700">{t('templates.selector.quoteLabel')}:</span>
                  <Badge>{currentTemplates.quoteTemplateDisplayName}</Badge>
                </p>
              </>
            )}
          </div>

          <Button
            onClick={handleSaveTemplates}
            loading={savePreferences.isPending}
            disabled={
              selectedInvoiceTemplate === currentTemplates?.invoiceTemplateId &&
              selectedQuoteTemplate === currentTemplates?.quoteTemplateId
            }
            icon={<Check className="w-5 h-5" />}
            size="lg"
          >
            {savePreferences.isPending ? t('templates.common.saving') : t('templates.selector.saveTemplates')}
          </Button>
        </div>
      </div>

      {/* Modal de prévisualisation */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          isOpen={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>
  );
};
